import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Stack,
  Chip
} from '@mui/material';
import { Search, History } from '@mui/icons-material';
import {
  useGetRecurringTaskByIdQuery,
  useGetGeneratedTicketsQuery
} from '../../../services/RecurringTaskService';
import { PageContainer, PageCard, SectionHeader, SectionPaper, GoBackButton } from '../../common/components/styled-ui-elements';
import WorklistGrid from '../../tickets/components/TicketList/WorklistGrid';

const RecurringTaskHistory: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const taskId = searchParams.get('id');
  const [searchText, setSearchText] = useState('');

  const { data: task, isLoading: isLoadingTask, error: taskError } = useGetRecurringTaskByIdQuery(taskId || '', {
    skip: !taskId,
  });

  const { data: tickets, isLoading: isLoadingTickets, error: ticketsError } = useGetGeneratedTicketsQuery(taskId || '', {
    skip: !taskId,
  });

  if (!taskId) {
    return (
      <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
        <Alert severity="error">
          No recurring task ID provided.
        </Alert>
      </PageContainer>
    );
  }

  if (isLoadingTask || isLoadingTickets) {
    return (
      <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (taskError || ticketsError) {
    return (
      <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
        <Alert severity="error">
          Failed to load recurring task history. Please try again.
        </Alert>
      </PageContainer>
    );
  }

  const getRecurrenceTypeLabel = (type: string): string => {
    switch (type) {
      case 'DAILY': return 'Daily';
      case 'WEEKLY': return 'Weekly';
      case 'MONTHLY': return 'Monthly';
      case 'CUSTOM': return 'Custom';
      default: return type;
    }
  };

  return (
    <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box sx={{
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <SectionHeader
                  icon={<History sx={{ mr: 1, color: '#3b82f6' }} />}
                  title="Generated Tickets History"
                />
                <Typography
                  variant="body2"
                  sx={{ color: '#64748b', ml: 4 }}
                >
                  Tickets generated from: {task?.templateTitle}
                </Typography>
              </Box>
              <GoBackButton />
            </Box>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Task Summary */}
            <SectionPaper sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Task Summary
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                    Template Title:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {task?.templateTitle}
                  </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                    Category:
                  </Typography>
                  <Typography variant="body2">
                    {task?.category}
                  </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                    Priority:
                  </Typography>
                  <Chip
                    label={task?.priority}
                    size="small"
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                    Recurrence:
                  </Typography>
                  <Typography variant="body2">
                    {task?.schedule && getRecurrenceTypeLabel(task.schedule.type)}
                    {task?.schedule.interval && task.schedule.interval > 1 && ` (every ${task.schedule.interval})`}
                  </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                    Status:
                  </Typography>
                  <Chip
                    label={task?.isActive ? 'Active' : 'Paused'}
                    size="small"
                    color={task?.isActive ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                    Total Tickets Generated:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {task?.generatedTicketCount || 0}
                  </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                    Next Scheduled:
                  </Typography>
                  <Typography variant="body2">
                    {task?.nextScheduledDate 
                      ? new Date(task.nextScheduledDate).toLocaleString() 
                      : 'Not scheduled'}
                  </Typography>
                </Box>
              </Stack>
            </SectionPaper>

            {/* Search Section */}
            <SectionPaper sx={{ mb: 3 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search tickets by title, description, category, status, or priority..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </SectionPaper>

            {/* Tickets Grid */}
            <SectionPaper>
              {tickets && tickets.length > 0 ? (
                <WorklistGrid
                  tickets={tickets}
                  searchText={searchText}
                  showClosedTickets={true}
                />
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    No tickets have been generated from this recurring task yet.
                  </Typography>
                </Box>
              )}
            </SectionPaper>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default RecurringTaskHistory;
